import { memo } from 'react';
import { ToolCallDisplay } from './ToolCallDisplay.js';
import type { ChatMessage as ChatMessageType } from './types.js';

interface ChatMessageProps {
  /** メッセージ */
  message: ChatMessageType;
  /** ストリーミング中かどうか */
  isStreaming?: boolean;
}

/**
 * Markdownのシンプルなレンダリング（改行とコードブロック）
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business Markdownテキストをコードブロック・インラインコード・改行に分割しReact要素配列に変換
 * @graph-connects none
 */
function renderContent(content: string) {
  // コードブロックの処理
  const parts = content.split(/(```[\s\S]*?```)/g);

  return parts.map((part, index) => {
    if (part.startsWith('```') && part.endsWith('```')) {
      // コードブロック
      const codeContent = part.slice(3, -3);
      const firstNewline = codeContent.indexOf('\n');
      const language = firstNewline > 0 ? codeContent.slice(0, firstNewline).trim() : '';
      const code = firstNewline > 0 ? codeContent.slice(firstNewline + 1) : codeContent;

      return (
        <pre
          key={index}
          className="my-2 p-3 bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded-lg overflow-x-auto"
        >
          {language && (
            <div className="mb-2 text-xs text-[var(--color-text-muted)]">{language}</div>
          )}
          <code className="text-xs font-mono text-[var(--color-text-primary)]">{code}</code>
        </pre>
      );
    }

    // 通常テキスト（インラインコードと改行を処理）
    return (
      <span key={index}>
        {part.split('\n').map((line, lineIndex, arr) => (
          <span key={lineIndex}>
            {renderInlineCode(line)}
            {lineIndex < arr.length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business テキスト内のバッククォート囲みをインラインコード要素に変換しそれ以外はプレーンテキストとして返却
 * @graph-connects none
 */
function renderInlineCode(text: string) {
  const parts = text.split(/(`[^`]+`)/g);

  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code
          key={index}
          className="px-1 py-0.5 bg-[var(--color-bg-base)] border border-[var(--color-border)] rounded text-xs font-mono"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チャットメッセージ表示。ユーザー/アシスタントメッセージをアバター付きで整形表示し、Markdown・コードブロック・ツールコール結果・ストリーミングカーソルに対応
 * @graph-connects ToolCallDisplay [calls] ツールコール表示コンポーネント呼び出し
 */
export const ChatMessage = memo(function ChatMessage({ message, isStreaming }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* アバター */}
      <div
        className={`
          flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
          ${
            isUser
              ? 'bg-[var(--color-accent-primary)] text-white'
              : 'bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)]'
          }
        `}
      >
        {isUser ? (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 8a3 3 0 100-6 3 3 0 000 6zM3.465 14.493a1.23 1.23 0 00.41 1.412A9.957 9.957 0 0010 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 00-13.074.003z" />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm4.75 6.5a.75.75 0 011.5 0v2.25h2.25a.75.75 0 010 1.5h-2.25v2.25a.75.75 0 01-1.5 0v-2.25H7a.75.75 0 010-1.5h2.25V8.5z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>

      {/* メッセージ本文 */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        <div
          className={`
            inline-block text-left px-4 py-3 rounded-2xl text-sm
            ${
              isUser
                ? 'bg-[var(--color-accent-primary)] text-white rounded-br-md'
                : 'bg-[var(--color-bg-surface)] border border-[var(--color-border)] text-[var(--color-text-primary)] rounded-bl-md'
            }
          `}
        >
          {/* ツール呼び出し */}
          {message.toolCalls && message.toolCalls.length > 0 && (
            <div className="mb-2">
              {message.toolCalls.map((toolCall) => (
                <ToolCallDisplay key={toolCall.id} toolCall={toolCall} />
              ))}
            </div>
          )}

          {/* メッセージ内容 */}
          <div className={isUser ? '' : 'prose prose-sm max-w-none'}>
            {renderContent(message.content)}
            {/* ストリーミング中のカーソル */}
            {isStreaming && !isUser && (
              <span className="inline-block w-2 h-4 ml-0.5 bg-current animate-pulse" />
            )}
          </div>
        </div>

        {/* タイムスタンプ */}
        <div className="mt-1 text-xs text-[var(--color-text-muted)]">
          {message.createdAt.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
});
